import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import { paperAPI } from '../services/api'
import { loadPaperOpenCounts } from '../utils/paperCounts'
import { krakenAPI, krakenError } from '../services/krakenApi'
import type { PaperPosition } from '../types'
import type { KrakenAccess, KrakenAccount, KrakenAccountHolding, KrakenAccountOrder, KrakenConnection, LiveTrade } from '../types/kraken'
import Spinner from '../components/Spinner'
import TradeTicketModal from '../components/trading/TradeTicketModal'
import ManageExitModal from '../components/trading/ManageExitModal'
import { LiveTradeStatusBadge, duration, pnlClass, signedUsd, usd } from '../components/trading/tradeFormat'
import { formatPrice } from '../utils/marketPrice'
import { displaySymbol } from '../utils/displaySymbol'

type Tab = 'open' | 'pending' | 'history' | 'paper'

const TABS: { key: Tab; label: string }[] = [
  { key: 'open', label: 'Open positions' },
  { key: 'pending', label: 'Pending orders' },
  { key: 'history', label: 'Trade history' },
  { key: 'paper', label: 'Paper trades' },
]

// Matches the backend poller's cadence closely enough to show fills promptly.
const POLL_MS = 15_000
// The account sync costs two private Kraken calls, so it refreshes less often.
const ACCOUNT_POLL_MS = 30_000

// Kraken's own funding page; deposits and withdrawals both start there.
const KRAKEN_FUNDING_URL = 'https://www.kraken.com/u/funding'

const price = (value: number | null | undefined) => (value == null ? '—' : `$${formatPrice(value)}`)

/**
 * Live Kraken trading, confirm-first. Every figure on this page after a
 * submission is Kraken's actual fill; FinCreator never moves funds, so
 * deposits and withdrawals link out to Kraken's own pages.
 */
const Trades = () => {
  const [access, setAccess] = useState<KrakenAccess | null>(null)
  const [conn, setConn] = useState<KrakenConnection | null>(null)
  const [account, setAccount] = useState<KrakenAccount | null>(null)
  const [accountError, setAccountError] = useState<string | null>(null)
  const [tab, setTab] = useState<Tab>('open')
  const [trades, setTrades] = useState<Record<Tab, LiveTrade[]>>({ open: [], pending: [], history: [], paper: [] })
  const [paper, setPaper] = useState<PaperPosition[] | null>(null)
  // The tab badge: open paper positions only, from the account, never the
  // length of the (capped, open + closed) list the tab shows.
  const [paperOpen, setPaperOpen] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [ticketSignal, setTicketSignal] = useState<number | null>(null)
  const [managing, setManaging] = useState<{ trade: LiveTrade; action?: 'close' } | null>(null)

  const loadTrades = useCallback(async () => {
    try {
      const [open, pending, history] = await Promise.all([
        krakenAPI.trades('open'),
        krakenAPI.trades('pending'),
        krakenAPI.trades('history'),
      ])
      setTrades({ open, pending, history, paper: [] })
    } catch {
      // keep the last good lists; the connection card reports Kraken problems
    }
  }, [])

  // The whole Kraken account (Balance + open orders), not only FinCreator's trades.
  const loadAccount = useCallback(async () => {
    try {
      setAccount(await krakenAPI.account())
      setAccountError(null)
    } catch (e) {
      setAccountError(krakenError(e, 'Could not sync the Kraken account.'))
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const a = await krakenAPI.access()
        if (cancelled) return
        setAccess(a)
        if (!a.allowed) return
        const c = await krakenAPI.connection()
        if (cancelled) return
        setConn(c)
        await Promise.all([loadTrades(), c.connected ? loadAccount() : Promise.resolve()])
      } catch {
        if (!cancelled) setAccess({ allowed: false })
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [loadTrades, loadAccount])

  useEffect(() => {
    if (!access?.allowed) return
    const tradesTimer = setInterval(loadTrades, POLL_MS)
    const accountTimer = conn?.connected ? setInterval(loadAccount, ACCOUNT_POLL_MS) : undefined
    return () => {
      clearInterval(tradesTimer)
      if (accountTimer) clearInterval(accountTimer)
    }
  }, [access, conn, loadTrades, loadAccount])

  useEffect(() => {
    loadPaperOpenCounts().then((counts) => setPaperOpen(counts?.open ?? null))
  }, [])

  useEffect(() => {
    if (tab === 'paper' && paper === null) {
      Promise.all([paperAPI.positions({ status: 'open', limit: 50 }), paperAPI.positions({ status: 'closed', limit: 50 })])
        .then(([o, c]) => setPaper([...o, ...c]))
        .catch(() => setPaper([]))
    }
  }, [tab, paper])

  const reviewLatest = async () => {
    try {
      const signal = await krakenAPI.latestSignal()
      if (!signal) {
        toast.error('No active long signal to review right now.')
        return
      }
      setTicketSignal(signal.id)
    } catch (e) {
      toast.error(krakenError(e, 'Could not load the latest signal.'))
    }
  }

  const replaceTrade = (next: LiveTrade) => {
    loadTrades()
    setManaging((m) => (m ? { ...m, trade: next } : m))
  }

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    )
  }

  if (!access?.allowed) {
    return (
      <div className="rounded-lg border border-terminal-border bg-terminal-surface p-8 text-center text-gray-300">
        Live trading is restricted to FinCreator admins.
      </div>
    )
  }

  const list = trades[tab]
  // Coins held on Kraken outside FinCreator (or beyond what its trades hold).
  // Shown in their own section, never counted as tracked positions. Sub-$1 dust
  // is hidden here (it still shows in the balances strip); unpriced holdings are
  // kept because we can't judge whether they're dust.
  const externalHoldings = (account?.holdings ?? []).filter(
    (h) => !h.is_cash && h.external_qty > 0 && (h.value_usd == null || h.value_usd >= 1)
  )
  const krakenOrders = account?.open_orders ?? []
  const counts: Record<Tab, number> = {
    // Only FinCreator-tracked live trades — wallet holdings live in their own section.
    open: trades.open.length,
    // The panel lists every open order on the account, so the count must too.
    pending: trades.pending.length + krakenOrders.length,
    history: trades.history.length,
    paper: paperOpen ?? 0,
  }
  const emptyTab = counts[tab] === 0

  return (
    <div className="space-y-6 text-gray-100">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-50">Trades</h1>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
            {conn?.connected ? (
              <span className="rounded-full bg-green-500/15 px-3 py-1 font-semibold text-green-300" data-testid="kraken-status">
                ● Kraken connected{conn.key_hint ? ` · key …${conn.key_hint}` : ''}
              </span>
            ) : (
              <Link
                to="/exchange"
                className="rounded-full bg-red-500/15 px-3 py-1 font-semibold text-red-300 hover:bg-red-500/25"
                data-testid="kraken-status"
              >
                ● Kraken not connected — connect →
              </Link>
            )}
            <span className="rounded-full border border-amber-400/50 bg-amber-500/10 px-3 py-1 font-semibold text-amber-200">
              Trading mode: Confirm first
            </span>
          </div>
        </div>
        <button
          type="button"
          onClick={reviewLatest}
          disabled={!conn?.connected}
          className="w-full rounded-md bg-primary-600 px-4 py-2 font-semibold text-white hover:bg-primary-700 disabled:opacity-50 sm:w-auto"
        >
          Review latest signal
        </button>
      </div>

      {conn && !conn.trading_enabled && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-100" role="note">
          <span className="font-semibold">Live trading is switched off</span> (KRAKEN_TRADING_ENABLED=false). You can
          review and validate tickets; no order of any kind will be sent to Kraken.
        </div>
      )}
      {conn && conn.error && (
        <div className="rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-200">{conn.error}</div>
      )}

      {/* Balance */}
      <section className="rounded-lg border border-terminal-border bg-terminal-surface p-4 md:p-6" aria-labelledby="bal-h">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 id="bal-h" className="text-xs font-medium uppercase tracking-wider text-gray-400">
              Kraken balance (live)
            </h2>
            <p className="mt-1 font-mono text-3xl font-bold tabular-nums text-gray-50" data-testid="usd-available">
              {account ? usd(account.usd_available) : '—'}
              <span className="ml-2 text-sm font-normal text-gray-400">USD available</span>
            </p>
            {account && (
              <p className="mt-0.5 text-xs text-gray-400">
                Account value ≈ <span className="font-mono tabular-nums text-gray-200">{usd(account.total_value_usd)}</span>
                {account.unpriced_assets.length > 0 && ` (not priced: ${account.unpriced_assets.join(', ')})`}
              </p>
            )}
          </div>
          <div className="flex gap-3 text-sm font-semibold">
            <a href={conn?.deposit_url ?? KRAKEN_FUNDING_URL} target="_blank" rel="noopener noreferrer" className="text-primary-400 hover:text-primary-300">
              Deposit on Kraken →
            </a>
            <a href={conn?.withdraw_url ?? KRAKEN_FUNDING_URL} target="_blank" rel="noopener noreferrer" className="text-primary-400 hover:text-primary-300">
              Withdraw on Kraken →
            </a>
          </div>
        </div>
        {accountError && <p className="mt-2 text-sm text-red-300">{accountError}</p>}
        {account && account.holdings.length > 0 && (
          <ul className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="holdings">
            {account.holdings.map((h) => (
              <li key={h.asset} className="rounded-md border border-terminal-border bg-terminal-bg px-3 py-2">
                <p className="flex items-center justify-between gap-1 text-xs text-gray-400">
                  <span>
                    {h.display}
                    {h.earn && <span className="text-gray-500"> · {h.earn}</span>}
                  </span>
                  {!h.is_cash && <SourceTag holding={h} />}
                </p>
                <p className="font-mono text-sm tabular-nums text-gray-100">{qty(h.balance)}</p>
                {h.value_usd != null && !h.is_cash && <p className="text-[11px] text-gray-400">≈ {usd(h.value_usd)}</p>}
                {h.on_hold > 0 && <p className="text-[11px] text-gray-500">{qty(h.on_hold)} on hold</p>}
              </li>
            ))}
          </ul>
        )}
        <p className="mt-3 text-[11px] text-gray-500">
          FinCreator never moves funds. Deposits and withdrawals happen on Kraken. Rails: max {usd(conn?.max_order_usd ?? 0)}/order ·
          max daily loss {usd(conn?.max_daily_loss_usd ?? 0)} · max {conn?.max_open_positions ?? 0} open.
        </p>
      </section>

      {/* Tabs */}
      <div role="tablist" aria-label="Trades" className="flex gap-1 overflow-x-auto rounded-lg border border-terminal-border bg-terminal-surface p-1">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => setTab(t.key)}
            className={`whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium ${
              tab === t.key ? 'bg-primary-600 text-white' : 'text-gray-400 hover:bg-white/5 hover:text-gray-200'
            }`}
          >
            {t.label}
            {counts[t.key] > 0 && <span className="ml-1.5 text-xs opacity-80">{counts[t.key]}</span>}
          </button>
        ))}
      </div>

      <div role="tabpanel">
        {tab === 'paper' ? (
          <PaperList positions={paper} />
        ) : tab === 'open' ? (
          <div className="space-y-6">
            {trades.open.length > 0 ? (
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                {trades.open.map((t) => (
                  <PositionCard
                    key={t.id}
                    trade={t}
                    onManage={() => setManaging({ trade: t })}
                    onClose={() => setManaging({ trade: t, action: 'close' })}
                  />
                ))}
              </div>
            ) : (
              <div
                className="rounded-lg border border-terminal-border bg-terminal-surface p-8 text-center"
                data-testid="no-tracked"
              >
                <p className="text-sm text-gray-400">No tracked positions.</p>
                {conn && !conn.trading_enabled && (
                  <p className="mt-1 text-xs text-gray-500">
                    Live trading is off — tracked positions will appear here once it's enabled.
                  </p>
                )}
              </div>
            )}

            {externalHoldings.length > 0 && (
              <section aria-labelledby="other-holdings-h">
                <div className="mb-3 border-t border-terminal-border pt-4">
                  <h3 id="other-holdings-h" className="text-xs font-medium uppercase tracking-wider text-gray-400">
                    Other Kraken holdings
                    <span className="ml-1.5 text-gray-500">{externalHoldings.length}</span>
                  </h3>
                  <p className="mt-1 text-[11px] text-gray-500">
                    Bought outside FinCreator and not tracked here — no entry price or P&amp;L.
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2" data-testid="other-holdings">
                  {externalHoldings.map((h) => (
                    <ExternalHoldingCard key={h.asset} holding={h} />
                  ))}
                </div>
              </section>
            )}
          </div>
        ) : emptyTab ? (
          <p className="rounded-lg border border-terminal-border bg-terminal-surface p-8 text-center text-sm text-gray-400">
            {tab === 'pending' ? 'No open orders on Kraken.' : 'No live trades yet.'}
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {list.map((t) =>
              tab === 'history' ? (
                <HistoryCard key={t.id} trade={t} />
              ) : (
                <PositionCard
                  key={t.id}
                  trade={t}
                  onManage={() => setManaging({ trade: t })}
                  onClose={() => setManaging({ trade: t, action: 'close' })}
                />
              )
            )}
          </div>
        )}
        {tab === 'pending' && krakenOrders.length > 0 && (
          <div className="mt-6">
            <h3 className="mb-2 text-xs font-medium uppercase tracking-wider text-gray-400">
              All open orders on your Kraken account
            </h3>
            <ul className="divide-y divide-terminal-border rounded-lg border border-terminal-border bg-terminal-surface" data-testid="kraken-orders">
              {krakenOrders.map((o) => (
                <KrakenOrderRow key={o.txid} order={o} />
              ))}
            </ul>
          </div>
        )}
      </div>

      {ticketSignal != null && (
        <TradeTicketModal
          signalId={ticketSignal}
          onClose={() => setTicketSignal(null)}
          onSubmitted={() => {
            loadTrades()
            loadAccount()
          }}
        />
      )}
      {managing && (
        <ManageExitModal
          trade={managing.trade}
          initialAction={managing.action}
          onClose={() => setManaging(null)}
          onChanged={replaceTrade}
        />
      )}
    </div>
  )
}

const qty = (value: number) => value.toLocaleString('en-US', { maximumFractionDigits: 8 })

/** A FinCreator-originated trade or order, with the signal it came from. */
const FinCreatorTag = ({ signalId }: { signalId: number | null }) => (
  <span className="rounded bg-primary-500/15 px-2 py-0.5 text-xs font-semibold text-primary-300" data-testid="fc-tag">
    FinCreator{signalId != null ? ` · signal #${signalId}` : ''}
  </span>
)

/** Held or placed directly on Kraken, outside FinCreator. */
const KrakenTag = () => (
  <span className="rounded bg-gray-500/20 px-2 py-0.5 text-xs font-semibold text-gray-300" data-testid="kraken-tag">
    Placed on Kraken
  </span>
)

const SourceTag = ({ holding: h }: { holding: KrakenAccountHolding }) =>
  h.source === 'kraken' ? (
    <span className="text-[10px] text-gray-500">Placed on Kraken</span>
  ) : (
    <span className="text-[10px] text-primary-300">
      {h.fincreator_trades
        .map((t) => (t.signal_id != null ? `#${t.signal_id}` : `trade ${t.trade_id}`))
        .join(', ')}
      {h.source === 'mixed' ? ' + Kraken' : ''}
    </span>
  )

/**
 * A coin held on Kraken outside FinCreator (or the part beyond what a
 * FinCreator trade holds). FinCreator never saw its purchase, so it shows the
 * quantity and today's value only -- no entry price, no P&L, no exit buttons.
 */
const ExternalHoldingCard = ({ holding: h }: { holding: KrakenAccountHolding }) => {
  const value = h.price_usd != null ? h.external_qty * h.price_usd : null
  return (
    <div className="rounded-lg border border-terminal-border bg-terminal-surface p-4" data-testid="external-holding">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-lg font-bold text-gray-50">{h.display}</span>
          {h.earn && <span className="text-xs text-gray-400">{h.earn}</span>}
          <KrakenTag />
        </div>
        <span className="rounded bg-gray-500/15 px-2 py-0.5 text-xs font-semibold text-gray-300">Held</span>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Field label="Quantity" value={qty(h.external_qty)} />
        <Field label="Current price" value={price(h.price_usd)} />
        <Field label="Value" value={value == null ? '—' : usd(value)} />
        {h.on_hold > 0 && <Field label="On hold (orders)" value={qty(h.on_hold)} />}
      </div>
      <p className="mt-3 text-[11px] text-gray-500">
        Bought outside FinCreator, so its entry price and P&amp;L aren't tracked here.
        {h.source === 'mixed' && ` A further ${qty(h.fincreator_qty)} belongs to a FinCreator trade, shown separately.`}
      </p>
    </div>
  )
}

const KrakenOrderRow = ({ order: o }: { order: KrakenAccountOrder }) => (
  <li className="flex flex-wrap items-center justify-between gap-2 px-4 py-2 text-sm" data-source={o.source}>
    <span className="font-mono text-gray-200">{o.description ?? `${o.type} ${o.volume} ${o.pair}`}</span>
    <span className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
      {o.source === 'fincreator' ? (
        <>
          <FinCreatorTag signalId={o.signal_id} />
          {o.fincreator_role && <span>{o.fincreator_role}</span>}
        </>
      ) : (
        <KrakenTag />
      )}
      <span className="font-mono">{o.txid}</span>
    </span>
  </li>
)

const Field = ({ label, value, valueClass = 'text-gray-100' }: { label: string; value: string; valueClass?: string }) => (
  <div>
    <p className="text-[11px] uppercase tracking-wider text-gray-500">{label}</p>
    <p className={`font-mono text-sm tabular-nums ${valueClass}`}>{value}</p>
  </div>
)

const PositionCard = ({ trade: t, onManage, onClose }: { trade: LiveTrade; onManage: () => void; onClose: () => void }) => {
  const holding = t.held_qty > 0
  return (
    <div className="rounded-lg border border-terminal-border bg-terminal-surface p-4" data-testid="position-card">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-lg font-bold text-gray-50">{displaySymbol(t.symbol)}</span>
          <span className="rounded bg-green-500/15 px-2 py-0.5 text-xs font-bold text-green-300">LONG</span>
          <FinCreatorTag signalId={t.signal_id} />
        </div>
        <LiveTradeStatusBadge trade={t} />
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Field
          label="Quantity"
          value={t.filled_qty < t.requested_qty ? `${t.held_qty} (of ${t.requested_qty})` : String(t.held_qty)}
        />
        <Field label="Avg entry (fill)" value={price(t.avg_entry_price)} />
        <Field label="Current price" value={price(t.live_price)} />
        <Field
          label="Unrealized P&L"
          value={
            t.unrealized_pnl == null
              ? '—'
              : `${signedUsd(t.unrealized_pnl)} (${t.unrealized_pct! >= 0 ? '+' : ''}${t.unrealized_pct!.toFixed(2)}%)`
          }
          valueClass={pnlClass(t.unrealized_pnl)}
        />
        <Field label={t.stop_on_kraken ? 'Stop (on Kraken)' : 'Stop'} value={price(t.stop_price)} valueClass="text-red-300" />
        <Field label="Target (watched)" value={price(t.target_price)} valueClass="text-green-300" />
        <Field label="Fees paid" value={usd(t.fees_total)} />
        <Field label="Time open" value={duration(t.opened_at ?? t.submitted_at)} />
        <Field label="Limit / signal" value={`${price(t.limit_price)}`} />
      </div>
      {holding && !t.stop_on_kraken && (
        <p className="mt-3 rounded-md border border-red-500/50 bg-red-500/10 p-2 text-sm font-semibold text-red-200" role="alert">
          ⚠ No stop on Kraken for this position. {t.status_detail}
        </p>
      )}
      {t.status_detail && (t.stop_on_kraken || !holding) && <p className="mt-3 text-xs text-amber-200">{t.status_detail}</p>}
      <p className="mt-3 truncate text-[11px] text-gray-500">
        Kraken order {t.entry_txid ?? '—'}
        {t.signal_id ? ` · signal #${t.signal_id}` : ''}
      </p>
      <div className="mt-3 flex gap-2">
        <button type="button" onClick={onManage} className="flex-1 rounded-md border border-terminal-border px-3 py-2 text-sm font-medium text-gray-200 hover:bg-white/5">
          Manage exit
        </button>
        {holding ? (
          <button type="button" onClick={onClose} className="flex-1 rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white hover:bg-red-700">
            Close position
          </button>
        ) : null}
      </div>
    </div>
  )
}

const REASON: Record<string, string> = { target: 'Target reached', stop: 'Stop reached', manual: 'Closed manually' }

const HistoryCard = ({ trade: t }: { trade: LiveTrade }) => (
  <div className="rounded-lg border border-terminal-border bg-terminal-surface p-4" data-testid="history-card">
    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-lg font-bold text-gray-50">{displaySymbol(t.symbol)}</span>
        <FinCreatorTag signalId={t.signal_id} />
      </div>
      <LiveTradeStatusBadge trade={t} />
    </div>
    {t.status === 'closed' ? (
      <>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <Field label="Entry (fill)" value={price(t.avg_entry_price)} />
          <Field label="Exit (fill)" value={price(t.avg_exit_price)} />
          <Field label="Quantity" value={String(t.exit_qty)} />
          <Field label="Gross" value={signedUsd(t.gross_pnl ?? 0)} valueClass={pnlClass(t.gross_pnl)} />
          <Field label="Fees" value={usd(t.fees_total)} />
          <Field label="Net" value={signedUsd(t.net_pnl ?? 0)} valueClass={pnlClass(t.net_pnl)} />
          <Field
            label="Return"
            value={t.return_pct == null ? '—' : `${t.return_pct >= 0 ? '+' : ''}${t.return_pct.toFixed(2)}%`}
            valueClass={pnlClass(t.return_pct)}
          />
          <Field label="Duration" value={duration(t.opened_at, t.closed_at)} />
          <Field label="Exit" value={REASON[t.exit_reason ?? ''] ?? '—'} />
        </div>
        {t.entry_partial && <p className="mt-2 text-xs text-amber-200">Entry was partially filled: {t.filled_qty} of {t.requested_qty}.</p>}
      </>
    ) : (
      <p className="text-sm text-gray-400">
        {t.status_detail ?? t.status_label}
        {t.kraken_error ? <span className="mt-1 block font-mono text-xs text-red-300">{t.kraken_error}</span> : null}
        <span className="mt-1 block text-xs text-gray-500">No position was opened — not counted as a win or a loss.</span>
      </p>
    )}
    <p className="mt-3 truncate text-[11px] text-gray-500">
      Kraken order {t.entry_txid ?? '—'}
      {t.signal_id ? ` · signal #${t.signal_id}` : ''}
      {t.closed_at ? ` · closed ${new Date(t.closed_at).toLocaleString()}` : ''}
    </p>
  </div>
)

const PaperList = ({ positions }: { positions: PaperPosition[] | null }) => {
  if (positions === null) {
    return (
      <div className="flex justify-center py-8">
        <Spinner />
      </div>
    )
  }
  if (positions.length === 0) {
    return <p className="rounded-lg border border-terminal-border bg-terminal-surface p-8 text-center text-sm text-gray-400">No paper trades yet.</p>
  }
  return (
    <div className="space-y-2">
      <p className="text-xs text-violet-300">Pretend money — the Paper forward test. Never mixed with live Kraken trades.</p>
      <ul className="divide-y divide-terminal-border rounded-lg border border-terminal-border bg-terminal-surface">
        {positions.map((p) => (
          <li key={p.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-2 text-sm">
            <span className="font-semibold text-gray-100">
              {displaySymbol(p.symbol)} <span className="text-xs font-normal text-gray-500">{p.window}d · {p.status}</span>
            </span>
            <span className="font-mono text-xs tabular-nums text-gray-300">
              {price(p.entry_price)} → {p.exit_price != null ? price(p.exit_price) : 'open'}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

export default Trades
